import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Calendar, Users, PlayCircle, Clock } from 'lucide-react';
import { Card, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/Button';
import { liveClassService } from '../../services/live-class.service';
import type { LiveClass } from '@edytech/shared/types';

const formatDate = (iso: string | null | undefined) => {
  if (!iso) return null;
  return new Date(iso).toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

export const LiveClassListPage: React.FC = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['live-classes'],
    queryFn: () => liveClassService.list({ limit: 50 }),
    // Keep the catalog fresh: refetch on window focus + poll so newly published
    // classes and session changes appear without a manual reload.
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
  });

  const classes: LiveClass[] = data?.data ?? [];

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="mb-10 text-center">
        <h1 className="text-3xl font-bold md:text-4xl">Live Classes</h1>
        <p className="mt-2 text-[rgb(var(--light-secondary))]">
          Join interactive, instructor-led sessions — live, online, and hands-on.
        </p>
      </div>

      {isLoading && (
        <div className="py-16 text-center text-[rgb(var(--light-secondary))]">Loading live classes...</div>
      )}

      {!isLoading && classes.length === 0 && (
        <div className="py-16 text-center text-[rgb(var(--light-secondary))]">
          No live classes available right now. Check back soon!
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {classes.map((liveClass) => {
          const next = formatDate(liveClass.nextStartsAt);
          return (
            <Card key={liveClass.id} className="flex flex-col">
              <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <PlayCircle className="h-6 w-6" />
              </div>
              <CardTitle className="text-lg">{liveClass.title}</CardTitle>
              {liveClass.description && (
                <p className="mt-1 line-clamp-2 text-sm text-[rgb(var(--light-secondary))]">
                  {liveClass.description}
                </p>
              )}

              <div className="mt-4 space-y-2 text-sm text-[rgb(var(--light-secondary))]">
                {liveClass.instructorName && (
                  <p className="flex items-center gap-2">
                    <Users className="h-4 w-4" /> {liveClass.instructorName}
                  </p>
                )}
                {next && (
                  <p className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" /> Next: {next}
                  </p>
                )}
                {liveClass.upcomingSessionCount ? (
                  <p className="flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    {liveClass.upcomingSessionCount} upcoming session
                    {liveClass.upcomingSessionCount === 1 ? '' : 's'}
                  </p>
                ) : null}
              </div>

              <div className="mt-4 flex items-center justify-between pt-2">
                <span className="text-sm font-medium">
                  {liveClass.capacity ? `${liveClass.capacity} seats` : 'Open'}
                </span>
                <Link to={`/live-classes/${liveClass.id}`}>
                  <Button size="sm">View &amp; Register</Button>
                </Link>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};
